import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  clearEmbeddingWaitRequest,
  getEmbeddingRequest,
  startEmbeddingWait,
} from '../utils/api';
import './ConversationIngestModal.css';

const TERMINAL = new Set(['finished', 'errored', 'canceled', 'cancelled']);

/**
 * Optional Wait vs Queue when search reported incomplete embeddings.
 */
export default function EmbeddingWaitModal({
  open,
  onClose,
  sessionId,
  waitRequest = null,
  defaultDeferredMessage = '',
  onWaitComplete = null,
  onQueueStarted = null,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [statusLine, setStatusLine] = useState('');
  const [deferredMessage, setDeferredMessage] = useState('');
  const [phase, setPhase] = useState('choose'); // choose | waiting
  const pollRef = useRef(null);

  const stopPoll = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      stopPoll();
      setBusy(false);
      setError('');
      setStatusLine('');
      setPhase('choose');
      setDeferredMessage(String(defaultDeferredMessage || waitRequest?.deferred_message || '').slice(0, 8000));
      return undefined;
    }
    setDeferredMessage(String(defaultDeferredMessage || waitRequest?.deferred_message || '').slice(0, 8000));
    setPhase('choose');
    setError('');
    setStatusLine('');
    return () => stopPoll();
  }, [open, defaultDeferredMessage, waitRequest, stopPoll]);

  const pollUntilDone = useCallback(async (requestId) => {
    if (!requestId) throw new Error('Missing embedding request id');
    return new Promise((resolve, reject) => {
      let tries = 0;
      const maxTries = 180; // ~6 min at 2s
      const tick = async () => {
        tries += 1;
        try {
          const row = await getEmbeddingRequest(requestId);
          const status = String(row?.status || row?.request?.status || '').toLowerCase();
          setStatusLine(status ? `Embedding status: ${status}` : 'Checking embedding…');
          if (TERMINAL.has(status)) {
            stopPoll();
            if (status === 'finished') resolve(row);
            else reject(new Error(`Embedding ${status}`));
            return;
          }
        } catch (err) {
          if (tries >= maxTries) {
            stopPoll();
            reject(err);
            return;
          }
        }
        if (tries >= maxTries) {
          stopPoll();
          reject(new Error('Timed out waiting for embedding'));
        }
      };
      tick();
      pollRef.current = setInterval(tick, 2000);
    });
  }, [stopPoll]);

  const handleDismiss = async () => {
    setBusy(true);
    setError('');
    try {
      if (sessionId) {
        try { await clearEmbeddingWaitRequest(sessionId); } catch (_) { /* ignore */ }
      }
      onClose?.();
    } finally {
      setBusy(false);
    }
  };

  const handleWait = async () => {
    if (!sessionId) return;
    setBusy(true);
    setError('');
    setPhase('waiting');
    setStatusLine('Queueing embedding…');
    try {
      const data = await startEmbeddingWait({ sessionId, mode: 'wait' });
      const requestId = data.request_id;
      setStatusLine('Waiting for embedding to finish…');
      await pollUntilDone(requestId);
      try { await clearEmbeddingWaitRequest(sessionId); } catch (_) { /* ignore */ }
      onWaitComplete?.(data);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Wait failed');
      setPhase('choose');
    } finally {
      setBusy(false);
      stopPoll();
    }
  };

  const handleQueue = async () => {
    if (!sessionId) return;
    const msg = String(deferredMessage || '').trim();
    if (!msg) {
      setError('Enter a message to send after embedding finishes.');
      return;
    }
    setBusy(true);
    setError('');
    setStatusLine('Queueing embedding…');
    try {
      const data = await startEmbeddingWait({
        sessionId,
        mode: 'queue',
        deferred_message: msg,
      });
      onQueueStarted?.({ ...data, deferred_message: msg });
      onClose?.();
    } catch (err) {
      setError(err.message || 'Failed to queue');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const toolLabel = waitRequest?.tool_slug || 'search tool';
  const reason = waitRequest?.reason ? ` (${waitRequest.reason})` : '';

  return (
    <div className="conversation-ingest-overlay" role="dialog" aria-modal="true" aria-labelledby="embedding-wait-title">
      <div className="conversation-ingest-modal" style={{ width: 'min(520px, 94vw)', minHeight: 'auto' }}>
        <div className="conversation-ingest-header">
          <h3 id="embedding-wait-title">Embeddings not ready</h3>
        </div>
        <div className="conversation-ingest-body">
          <p>
            We are not done embedding for <strong>{toolLabel}</strong>{reason}.
            Would you like to wait, or queue a message to send after it is done?
          </p>
          {phase === 'choose' && (
            <label className="conversation-ingest-hint" style={{ display: 'block', marginTop: 12 }}>
              Message to send after embedding (Queue)
              <textarea
                value={deferredMessage}
                onChange={(e) => setDeferredMessage(e.target.value)}
                rows={3}
                disabled={busy}
                style={{ width: '100%', marginTop: 6, resize: 'vertical' }}
                placeholder="Your question or follow-up…"
              />
            </label>
          )}
          {statusLine ? <p className="conversation-ingest-hint" style={{ marginTop: 12 }}>{statusLine}</p> : null}
          {error ? <p style={{ color: '#b91c1c', marginTop: 12 }}>{error}</p> : null}
        </div>
        <div className="conversation-ingest-footer" style={{ flexWrap: 'wrap', gap: 8 }}>
          <button type="button" className="btn ghost" onClick={handleDismiss} disabled={busy}>
            Dismiss
          </button>
          <button type="button" className="btn ghost" onClick={handleQueue} disabled={busy || phase === 'waiting'}>
            Queue message
          </button>
          <button type="button" className="btn" onClick={handleWait} disabled={busy}>
            {phase === 'waiting' ? 'Waiting…' : 'Wait'}
          </button>
        </div>
      </div>
    </div>
  );
}
